import * as React from "react";
import Markdown, { Options } from "react-markdown";
import remarkGfm from "remark-gfm";
import { Citation } from "../../../platform";
import CitationBadge from "./CitationBadge";
import { collapseCitationRuns, overflowNumbers } from "../utils/citationRuns";

interface MessageMarkdownProps {
  content: string;
  citations?: Citation[];
  /** Scroll the host to a citation. Supplied by the caller, which holds the adapter. */
  onNavigate: (citation: Citation) => void;
}

/**
 * The "+N" that stands in for the tail of a long run of citations, opening in place
 * into the badges it hid. In place rather than in a popover, because the reader who
 * wants the eighth source of a sentence is reading that sentence.
 */
const CitationOverflow: React.FC<{
  numbers: number[];
  citations?: Citation[];
  onNavigate: (citation: Citation) => void;
}> = ({ numbers, citations, onNavigate }) => {
  const [open, setOpen] = React.useState(false);

  if (!open) {
    return (
      <span
        role="button"
        aria-label={`Show ${numbers.length} more sources`}
        title={`${numbers.length} more sources`}
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        className="pl-0.5"
      >
        <CitationBadge n={numbers.length} label={`+${numbers.length}`} interactive />
      </span>
    );
  }

  return (
    <>
      {numbers.map((n) => {
        const cit = citations?.[n - 1];
        if (!cit) return null;
        return (
          <span
            key={n}
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              onNavigate(cit);
            }}
            className="pl-0.5"
            title={cit.label}
          >
            <CitationBadge n={n} interactive />
          </span>
        );
      })}
    </>
  );
};

/**
 * The prose styling, as descendant selectors on the wrapper: react-markdown renders
 * plain elements, and styling them from the one wrapper keeps every override below
 * about behaviour (citations, links) rather than looks.
 *
 * Tailwind's preflight strips lists of their markers and links of their colour and
 * underline, which a chat reply needs back, so both are restored here.
 */
const PROSE = [
  "min-w-0 text-[13px] leading-[1.55] [word-break:break-word]",
  "[&>:first-of-type]:mt-0 [&>:last-child]:mb-0",
  "[&_p]:m-[0_0_0.6em]",
  "[&_:is(ul,ol)]:m-[0_0_0.6em] [&_:is(ul,ol)]:pl-[1.4em] [&_ul]:list-disc [&_ol]:list-decimal",
  "[&_li]:mb-[0.2em] [&_li>p]:m-0",
  "[&_:is(h1,h2,h3,h4)]:m-[0.8em_0_0.3em] [&_:is(h1,h2,h3,h4)]:text-[13px] [&_:is(h1,h2,h3,h4)]:font-semibold [&_:is(h1,h2,h3,h4)]:text-strong",
  "[&_blockquote]:m-[0_0_0.6em] [&_blockquote]:border-l-2 [&_blockquote]:border-input [&_blockquote]:pl-[0.9em] [&_blockquote]:text-muted-foreground",
  "[&_code]:rounded-xs [&_code]:bg-grey-100 [&_code]:px-1 [&_code]:py-px [&_code]:text-[0.92em] [&_code]:[font-family:'Cascadia_Code',Consolas,'SF_Mono',Menlo,monospace]",
  "[&_pre]:m-[0_0_0.6em] [&_pre]:overflow-x-auto [&_pre]:rounded-[12px] [&_pre]:border [&_pre]:border-border [&_pre]:bg-grey-50 [&_pre]:p-2.5",
  "[&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-[12px]",
  "[&_hr]:m-[0.8em_0] [&_hr]:border-t [&_hr]:border-border",
  "[&_table]:m-[0_0_0.8em] [&_table]:block [&_table]:max-w-full [&_table]:border-collapse [&_table]:border-spacing-0 [&_table]:overflow-x-auto [&_table]:text-[12px]",
  "[&_:is(th,td)]:border [&_:is(th,td)]:border-border [&_:is(th,td)]:px-2.5 [&_:is(th,td)]:py-[5px] [&_:is(th,td)]:text-left [&_:is(th,td)]:align-top",
  "[&_th]:bg-grey-50 [&_th]:font-semibold [&_th]:text-strong",
  "[&_a]:text-primary [&_a]:underline",
].join(" ");

// Strikethrough only from `~~`. A single tilde is how a model writes "roughly" --
// "~£43/seat ... = ~£48" -- and GFM's default struck out everything between the two.
const REMARK_PLUGINS: Options["remarkPlugins"] = [[remarkGfm, { singleTilde: false }]];

/**
 * An assistant message rendered as Markdown, with `[[n]](#cite-n)` links turned back
 * into clickable citations.
 *
 * Split out of ChatMessageBubble so the prose styling and the link interception live in
 * one place rather than inside a component that is also drawing edit cards, a header
 * and a footer. Colours are tokens, not values, so they follow the colour scheme.
 */
const MessageMarkdown: React.FC<MessageMarkdownProps> = ({ content, citations, onNavigate }) => (
  <div className={PROSE}>
    <Markdown
      remarkPlugins={REMARK_PLUGINS}
      components={{
        // _node is pulled out only to keep react-markdown's AST node out of the rest
        // spread below; it must not reach the rendered anchor.
        a: ({ node: _node, href, children, ...props }) => {
          const hidden = overflowNumbers(href);
          if (hidden) {
            return (
              <CitationOverflow numbers={hidden} citations={citations} onNavigate={onNavigate} />
            );
          }
          const citeMatch = href && /^#cite-(\d+)$/.exec(href);
          if (citeMatch) {
            const n = parseInt(citeMatch[1], 10);
            const cit = citations?.[n - 1];
            if (cit) {
              // The link text is "[n]"; the badge draws the number itself, so the
              // brackets are dropped rather than rendered inside it.
              return (
                <span
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onNavigate(cit);
                  }}
                  className="pl-0.5"
                  title={cit.label}
                >
                  <CitationBadge n={n} interactive />
                </span>
              );
            }
          }
          return (
            <a href={href} {...props} onClick={(e: React.MouseEvent) => e.preventDefault()}>
              {children}
            </a>
          );
        },
      }}
    >
      {collapseCitationRuns(content)}
    </Markdown>
  </div>
);

export default MessageMarkdown;
